/** CreatorInsightsDrawer — slide-over drawer opened when a creator row's "Detailed Analysis" button is clicked. */
import React from 'react';
import {
  useCampaignUiStore,
  useCreatorAnalyticsMetrics,
  useCampaignCapabilitiesStore,
} from '@discovr/features';
import { resolvePlatform } from '@discovr/types';
import { Sheet, SheetContent, SheetTitle } from '../../../components/ui/sheet';
import { DrawerHeader } from './DrawerHeader';
import { AnalyticsTab } from './AnalyticsTab';
import { BrandSafetyTab } from './BrandSafetyTab';
import { useCampaignPanelControls } from '../../useCampaignPanelControls';
import { resolveInsightsTab } from '../../utils/campaignPipeline';

const VALID_TABS = ['overview', 'brand-safety'] as const;
type InsightsTab = (typeof VALID_TABS)[number];

// Subset of the Phyllo profile object read for the drawer header. Sourced from
// the analytics cache (loosely typed upstream), so only the fields consumed here
// are declared; all are optional since older/pending payloads may omit them.
interface PhylloProfile {
  location?: { city?: string; state?: string; country?: string };
  is_protected?: boolean;
  platform_account_type?: string | null;
  age_group?: string | null;
  updated_at?: string | null;
  subscriber_count?: number | null;
  follower_count?: number | null;
  language?: string | null;
  gender?: string | null;
}

export function CreatorInsightsDrawer() {
  const { deepAnalysisCreator, deepAnalysisTab, campaignPipeline } = useCampaignUiStore();
  const campaignPlatform = useCampaignUiStore((s) => s.campaignPlatform);
  const setApprovalRequest = useCampaignUiStore((s) => s.setApprovalRequest);
  const setRejectRequest = useCampaignUiStore((s) => s.setRejectRequest);
  const { closePanels, setInsightsTab, openConversation } = useCampaignPanelControls();
  const lane = useCampaignCapabilitiesStore((s) => s.lane);
  const isAdmin = lane === 'admin';

  // All hooks must run unconditionally (before the early return); entryId falls
  // back to '' which disables the analytics query (enabled: !!entryId).
  const entryId = deepAnalysisCreator ? String(deepAnalysisCreator.id ?? '') : '';
  // Hold both queries until the platform resolves.
  const platform = resolvePlatform(campaignPlatform);
  const platformKnown = platform !== null;
  const { data: analyticsEnvelope, isLoading: analyticsLoading, isFetching: analyticsFetching } =
    useCreatorAnalyticsMetrics(entryId, platformKnown);

  if (!deepAnalysisCreator) return null;

  const activeTab: InsightsTab = resolveInsightsTab(
    VALID_TABS.includes(deepAnalysisTab as InsightsTab) ? (deepAnalysisTab as InsightsTab) : 'overview',
    campaignPipeline,
  );

  // Read Phyllo location from cache (no extra fetch — AnalyticsTab uses the same key).
  const phylloData = analyticsEnvelope?.status === 'done' ? (analyticsEnvelope.data ?? {}) as Record<string, unknown> : null;
  const phylloProfile = (phylloData?.profile ?? phylloData) as PhylloProfile | null;
  const phylloLocation = phylloProfile?.location
    ? [phylloProfile.location.city, phylloProfile.location.state, phylloProfile.location.country].filter(Boolean).join(', ')
    : undefined;
  // Demographics stats live on phylloProfile, which only populates once the
  // BE job reaches 'done'. Show skeletons while the first fetch is in
  // flight or while still actively polling a pending/processing job; once
  // polling gives up (2 min cap, see useCreatorAnalyticsMetrics) isFetching
  // goes false and this falls back to the original "no data" empty state
  // instead of a skeleton that never resolves.
  const demographicsLoading =
    analyticsLoading || (analyticsEnvelope?.status !== 'done' && analyticsFetching);

  const handleClose = () => closePanels();

  // Approve / Reject actions show only for a creator still awaiting brand decision.
  const canApproveReject =
    (deepAnalysisCreator as { status?: string } | null)?.status === 'pending';

  // First demographics stat is "Subscribers" on YouTube, "Followers" on Instagram
  // (mirrors the Reputation section, which is already platform-aware).
  const subscribersLabel = (campaignPlatform ?? '').toLowerCase().includes('youtube')
    ? 'Subscribers'
    : 'Followers';

  return (
    <Sheet open onOpenChange={(open) => { if (!open) handleClose(); }}>
      <SheetContent
        side="right"
        showCloseButton={false}
        // 1049px cap (Figma "Brand Safety/ sentiment" panel width), offset from
        // the app rail on wide screens; full-width on mobile. Plain inline
        // style, not a Tailwind class — Tailwind's JIT never generates a rule
        // for a nested min()/calc() arbitrary-value class (verified: no
        // matching rule in any loaded stylesheet), silently falling back to
        // content width. max-[992px]:w-full! still wins on narrow viewports
        // via !important, which beats an inline style.
        // Flush (p-0/gap-0) so DrawerHeader + the scroll body own their spacing.
        style={{ width: 'min(1049px, calc(100vw - 13rem))' }}
        className="gap-0 p-0 sm:max-w-none max-[992px]:w-full!"
        aria-label="Creator insights"
      >
        {/* Radix requires a title for the dialog; the visible identity lives in
            DrawerHeader, so this one is screen-reader-only. */}
        <SheetTitle className="sr-only">Creator insights</SheetTitle>

        {/* Sticky creator card — contains identity, stats, tab switcher, close */}
        <DrawerHeader
          creator={deepAnalysisCreator}
          activeTab={activeTab}
          onTabChange={(t) => setInsightsTab(t as import('@discovr/features').DeepAnalysisTab)}
          onClose={handleClose}
          campaignPlatform={campaignPlatform ?? undefined}
          location={phylloLocation}
          isCurated={isAdmin && phylloData?.is_part_of_creator_list === true}
          isProtected={isAdmin && phylloProfile?.is_protected === true}
          accountType={isAdmin ? (phylloProfile?.platform_account_type ?? null) : null}
          ageGroup={phylloProfile?.age_group ?? null}
          updatedAt={isAdmin ? (phylloProfile?.updated_at ?? null) : null}
          subscriberCount={phylloProfile?.subscriber_count ?? phylloProfile?.follower_count ?? null}
          language={phylloProfile?.language ?? null}
          gender={phylloProfile?.gender ?? null}
          subscribersLabel={subscribersLabel}
          demographicsLoading={demographicsLoading}
          canApproveReject={canApproveReject}
          onApprove={() => setApprovalRequest(deepAnalysisCreator)}
          onReject={() => setRejectRequest(deepAnalysisCreator)}
          onMessage={() => openConversation(deepAnalysisCreator)}
        />

        {/* Scrollable tab content */}
        <div className="flex-1 overflow-y-auto bg-muted px-6 py-5">
          {activeTab === 'overview' && (
            <AnalyticsTab entryId={entryId} creator={deepAnalysisCreator} />
          )}

          {activeTab === 'brand-safety' && <BrandSafetyTab creator={deepAnalysisCreator} />}
        </div>
      </SheetContent>
    </Sheet>
  );
}
